/**
 * Service d'inscription Mobile SSO v1.0
 * Architecture Frontend-First : Appels directs à l'IAM via nativeAuth
 * 
 * @version 2.7.9
 */

import { nativeAuthService } from './nativeAuth';
import { 
  ApiError, 
  fetchWithTimeout, 
  getHeaders,
  getNativeAuthConfig, 
  isDebugMode,
  ensureDeviceContext,
} from './api';

import type {
  MobileRegistrationFormData,
  MobileVerifyOtpResponse,
  MobileRegistrationCompleteResponse,
  MobileResendOtpResponse,
} from '../types/mobile';
import type { NativeInitResponse } from '../types/native';

export const mobileRegistrationService = {
  /**
   * Init registration via nativeAuth.register() (Frontend-First)
   */
  async init(
    data: Omit<MobileRegistrationFormData, 'password' | 'password_confirmation'>
  ): Promise<NativeInitResponse> {
    if (!nativeAuthService.hasCredentials()) {
      await nativeAuthService.loadCredentials();
    }

    return nativeAuthService.register({
      name: data.name,
      email: data.email,
      ccphone: data.ccphone,
      phone: data.phone,
      town: data.town,
      country: data.country,
      registration_type: data.registration_type,
    });
  },

  /**
   * Verify OTP via nativeAuth.validate()
   */
  async verifyOtp(
    processToken: string,
    otpCode: string
  ): Promise<MobileVerifyOtpResponse> {
    const config = getNativeAuthConfig();
    await ensureDeviceContext();

    if (isDebugMode()) {
      console.log('📤 [IAM] POST /sso/native/validate (registration OTP)');
    }

    return fetchWithTimeout<MobileVerifyOtpResponse>(
      `${config.iamApiUrl}/sso/native/validate`,
      {
        method: 'POST',
        headers: getHeaders(undefined, false),
        body: JSON.stringify({
          process_token: processToken,
          otp_code: otpCode,
        }),
      },
      config.timeout || 30000
    );
  },

  /**
   * Complete registration with password
   */
  async complete(
    processToken: string,
    password: string
  ): Promise<MobileRegistrationCompleteResponse> {
    const config = getNativeAuthConfig();
    await ensureDeviceContext();

    if (isDebugMode()) {
      console.log('📤 [IAM] POST /sso/native/validate (registration complete)');
    }

    return fetchWithTimeout<MobileRegistrationCompleteResponse>(
      `${config.iamApiUrl}/sso/native/validate`,
      {
        method: 'POST',
        headers: getHeaders(undefined, false),
        body: JSON.stringify({
          process_token: processToken,
          password,
          password_confirmation: password,
        }),
      },
      config.timeout || 30000
    );
  },

  /**
   * Complete phone-only registration (no password)
   */
  async completePhoneOnly(
    processToken: string
  ): Promise<MobileRegistrationCompleteResponse> {
    const config = getNativeAuthConfig();
    await ensureDeviceContext();

    if (isDebugMode()) {
      console.log('📤 [IAM] POST /sso/native/validate (phone-only complete)');
    }

    return fetchWithTimeout<MobileRegistrationCompleteResponse>(
      `${config.iamApiUrl}/sso/native/validate`,
      {
        method: 'POST',
        headers: getHeaders(undefined, false),
        body: JSON.stringify({
          process_token: processToken,
        }),
      },
      config.timeout || 30000
    );
  },

  /**
   * Resend OTP
   */
  async resendOtp(processToken: string): Promise<MobileResendOtpResponse> {
    return nativeAuthService.resendOtp(processToken) as unknown as MobileResendOtpResponse;
  },
};

export default mobileRegistrationService;
